import {useEffect, useState} from 'react';
import {useSelector, useDispatch, shallowEqual} from 'react-redux';
import {voteForPollOption} from '../../reducers/apiActions';
import {trackPollVote} from '../../utils/analytics';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';

import {PollLineIcon} from '~library/svg/PollLineIcon';
import {CheckmarkCircleIcon} from '~library/svg/CheckmarkCircleIcon';
import {DeleteBinLineIcon} from '~library/svg/DeleteBinLineIcon';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {MealOptionsIcon} from '~library/svg/MealOptionsIcon';

import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {Radio} from '~library/atoms/Radio';
import {isMealOptionsEventType} from '~/common/utils/event_utils';
import {isPreview} from '../../utils';
import {PollChoiceOverlay} from '../../common/PollChoiceOverlay';
import {selectIsMealOptionsEventType} from '~/common/fabric/reducers/widgets/selectors';
import {largishAndUp, emerald, link} from '~libSass/base/_exports.sass';

export const PollModuleHost = ({
  readonly,
  onRemove,
  restored,
  onPropagateRestore,
  noOverlay,
  ...props
}) => {
  const {options, question, pollId, optionSelectedByUser, voted, hasRSVPed} = useSelector(
    ({poll: p, rsvp}) => ({
      options: p.options?.map((o, i) => ({
        key: o.id || i,
        choice: o.option || '',
        response_count: o.response_count || '',
      })),
      question: p.question,
      pollId: p.id,
      voted: p.voted,
      optionSelectedByUser: p.option_selected_by_user,
      hasRSVPed: rsvp.hasRsvpd,
    })
  );

  const isHost = useSelector((state) => state.user?.isHost);
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const widgets = useSelector((state) => state.eventDetails?.widgets);
  const eventType = useSelector((state) => state.eventDetails?.eventType);
  const pollOptions = useSelector((state) => state.poll?.options);
  const showMealOptionsEventType = isMealOptionsEventType(eventType);
  const isMealOptions = useSelector(selectIsMealOptionsEventType);
  const showMealOptions = showMealOptionsEventType || isMealOptions;
  const [selectedChoice, setSelectedChoice] = useState(false);
  const [initialState, setInitialState] = useState(true);
  const [changingChoice, setChangingChoice] = useState(false);
  const [pollOverlayChoice, setPollOverlayChoice] = useState('');
  const [pollOverlayOpen, setPollOverlayOpen] = useState(false);
  const [guestsWhoVotedThis, setGuestsWhoVotedThis] = useState([]);
  const {matches: largeScreens} = useMatchQuery(`(min-width: ${largishAndUp})`);

  const {rsvpDetails} = useSelector(
    ({guestList: s}) => ({rsvpDetails: s.rsvpDetails}),
    shallowEqual
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (!optionSelectedByUser) {
      setChangingChoice(true);
    }
    setSelectedChoice(optionSelectedByUser);
  }, [optionSelectedByUser]);

  const submitPollVote = () => {
    trackPollVote('polls_vote');
    dispatch(
      voteForPollOption(pollId, 'cast_vote', {option: selectedChoice}, () => {
        setChangingChoice(false);
      })
    );
  };

  const changeChoice = () => {
    setChangingChoice(true);
    setInitialState(false);
  };

  const preview = isPreview();

  const openPollOverlay = (choice) => {
    setGuestsWhoVotedThis(() => {
      const relevantGuests = pollOptions.filter((userChoice) => userChoice.option === choice)[0]
        .response_user_ids;

      const relevantGuestNames = [];
      const allRsvps = [...rsvpDetails.yes, ...rsvpDetails.maybe, ...rsvpDetails.no];
      for (let i = 0; i < relevantGuests.length; i++) {
        const filteredGuest = allRsvps.filter((x) => x.user_id === relevantGuests[i]);
        if (filteredGuest.length !== 0) {
          relevantGuestNames.push(filteredGuest[0]);
        }
      }
      return relevantGuestNames;
    });

    setPollOverlayChoice(choice);
    setPollOverlayOpen(true);
  };

  useEffect(() => {
    if (restored && preview && isHost) {
      // API call to restore module
      const patchedEventData = {
        widgets,
      };
      onPropagateRestore(patchedEventData);
    }
  }, [restored]);

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    !voted ? setChangingChoice(true) : setInitialState(false);
  }, []);

  /* eslint-disable no-nested-ternary,react/button-has-type */
  return (
    <div className="view-invitation__poll-module">
      {preview && !readonly && (
        <div className="view-invitation__preview-cta multi">
          <a
            className="view-invitation__preview-cta"
            href={`/invitation/${eventId}/details`}
            data-qa-id="previewCtaEdit"
            ref={(elem) => {
              if (props.connectCTA) props.connectCTA('edit-poll', elem);
            }}
          >
            <EditLineIcon ratio={0.5} color={link} />
            Edit Poll
          </a>
          <span>|</span>
          <button
            className="unstyled-button view-invitation__preview-cta"
            ref={(elem) => props.connectCTA && props.connectCTA('remove-poll', elem)}
            data-qa-id="previewCtaRemove"
            id="remove-poll"
          >
            <DeleteBinLineIcon ratio={0.5} color={link} />
            Remove
          </button>
        </div>
      )}
      <div className="view-invitation__poll-module__main-contents">
        <div className="view-invitation__title-with-icon-wrapper">
          {showMealOptions ? <MealOptionsIcon ratio={0.75} /> : <PollLineIcon ratio={0.75} />}
          <Typography
            variant="header3"
            data-qa-id={showMealOptions ? 'meal-options-module-title' : 'poll-module-title'}
          >
            {showMealOptions ? 'Meal options' : 'Poll'}
          </Typography>
        </div>
        {question && (
          <Typography
            variant="paragraph3"
            color="pebble"
            classNames="view-invitation__poll-module__poll-question"
            data-qa-id="poll-question"
          >
            {question}
          </Typography>
        )}
        {initialState || !changingChoice ? (
          <div
            className="view-invitation__poll-module__poll-result-grid"
            data-qa-id="poll-module-host"
          >
            {options?.map((o) => (
              <div
                className={`view-invitation__poll-module__poll-result ${
                  o.key === optionSelectedByUser ? 'winner' : ''
                }`}
                key={`pollOption_${o.key}`}
              >
                <div className="view-invitation__poll-module__poll-result-name">
                  <Typography variant="paragraph2" data-qa-id="poll-option" data-qa-value={o.key}>
                    {o.choice}
                  </Typography>
                  {o.key === optionSelectedByUser && (
                    <CheckmarkCircleIcon ratio={0.75} color={emerald} />
                  )}
                </div>
                <div className="view-invitation__poll-module__results-view">
                  <Typography
                    variant="paragraph2"
                    classNames="view-invitation__poll-module__result-count"
                    data-qa-id="poll-option-response-count"
                    data-qa-value={o.response_count}
                  >
                    {o.response_count}
                  </Typography>
                  <Button
                    data-qa-id="view-poll-choice-overlay"
                    variant="link"
                    size="small"
                    onClick={() => openPollOverlay(o.choice)}
                    disabled={preview}
                  >
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="view-invitation__poll-module__poll-options">
            {options?.map((o) => (
              <Radio
                onChange={() => setSelectedChoice(o.key)}
                data-qa-id="poll-option"
                data-qa-value={o.key}
                checked={selectedChoice === o.key}
                label={o.choice}
                id={`pollOption_${o.key}`}
                key={o.key}
                name={`pollOption_${o.key}`}
                disabled={preview || !hasRSVPed}
              />
            ))}
          </div>
        )}
      </div>
      <div className="view-invitation__poll-module__vote-cta-wrapper">
        <Button
          data-qa-id={changingChoice ? 'submit-choice' : 'change-my-choice'}
          variant="primary"
          size={largeScreens ? 'large' : 'small'}
          onClick={initialState ? changeChoice : changingChoice ? submitPollVote : changeChoice}
          noTextWrap
          disabled={preview || (!initialState && !selectedChoice)}
        >
          {initialState ? 'Add Your Vote' : changingChoice ? 'Submit Choice' : 'Change My Choice'}
        </Button>
        {!preview && !hasRSVPed && (
          <Typography variant="paragraph3" color="pebble">
            You have to RSVP to vote.
          </Typography>
        )}
      </div>
      {noOverlay || (
        <PollChoiceOverlay
          choice={pollOverlayChoice}
          open={pollOverlayOpen}
          onClose={() => setPollOverlayOpen(false)}
          guests={guestsWhoVotedThis}
        />
      )}
    </div>
  );
};
